// Screen 16 · Contacts (Iris's pipeline screen 6, 2026-10-19): who they are and every opportunity they have been on,
// won and lost included, with the household details. A contact can sit on many opportunities. Users and permissions
// live under Settings → Users (Users.tsx).

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCrumb } from '../frame/Frame';
import { deleteRow, insertRow, updateRow, useRows, type Row } from '../lib/api';
import { age, contactName, monthDay, shortDate } from '../lib/format';
import { seatWords, usePipeline, type Lead, type Pipeline } from '../lib/pipeline';
import { useSession } from '../lib/session';
import { Btn, Edit, Empty, Ln, Loading, Panel } from '../ui/kit';
import { LeadValue, Strip } from './pipeline/bits';

/** The Contacts destination. */
export function People() {
  useCrumb([{ label: 'Contacts' }]);
  return <Contacts />;
}

// ---------- contacts ----------

function Contacts() {
  const s = useSession();
  const [params, setParams] = useSearchParams();
  const contacts = useRows('contact', { order: ['last_name', 'company', 'created_at'] });
  const [filter, setFilter] = useState('');
  const id = params.get('contact');
  const selected = contacts.rows.find((c) => c.id === id);
  const pick = (cid: string | null) => {
    const next = new URLSearchParams(params);
    if (cid) next.set('contact', cid);
    else next.delete('contact');
    setParams(next, { replace: true });
  };
  const shown = contacts.rows.filter((c) => !filter || `${contactName(c)} ${c.preferred_name ?? ''} ${c.company ?? ''} ${c.type ?? ''} ${c.email ?? ''}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="listdetail">
      <Panel
        label={`Contacts (${contacts.rows.length})`}
        right={
          <Btn
            small
            onClick={async () => {
              const types = s.setting<string[]>('contact_types', []);
              pick((await insertRow('contact', { first_name: 'New', last_name: 'contact', type: types[0] ?? null })).id);
            }}
          >
            + Contact
          </Btn>
        }
      >
        <input type="search" placeholder="Filter contacts" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
        <div className="list">
          {contacts.loading && <Loading />}
          {!contacts.loading && shown.length === 0 && <Empty>No contacts yet.</Empty>}
          {shown.map((c) => (
            <Ln
              key={c.id}
              sel={c.id === id}
              onClick={() => pick(c.id)}
              left={
                <>
                  {contactName(c)}
                  <br />
                  <small>{[c.company && contactName(c) !== c.company ? c.company : null, c.type].filter(Boolean).join(' · ')}</small>
                </>
              }
            />
          ))}
        </div>
      </Panel>
      {selected ? <ContactDetail key={selected.id} contact={selected} contacts={contacts.rows} onDeleted={() => pick(null)} /> : <Panel><Empty>Pick a contact to see the person, the household, and every opportunity they have been on.</Empty></Panel>}
    </div>
  );
}

function ContactDetail({ contact: c, contacts, onDeleted }: { contact: Row; contacts: Row[]; onDeleted: () => void }) {
  const s = useSession();
  const p = usePipeline();
  const notes = useRows('contact_note', { eq: { contact_id: c.id }, order: ['-noted_at'] });
  const types = s.setting<string[]>('contact_types', []);
  const [note, setNote] = useState('');
  const canEdit = s.canEdit('screen.contacts');
  const save = (patch: Row) => updateRow('contact', c.id, patch);

  const mine = p.leads.filter((l) => l.contacts.some((x) => x.id === c.id) || l.opp.primary_contact_id === c.id);
  const won = mine.filter((l) => l.outcome === 'won').length;
  const lost = mine.filter((l) => l.outcome === 'lost').length;
  const open = mine.filter((l) => l.outcome === 'open' || l.outcome === 'parked').length;
  const kids = (c.kids as { name: string; birthdate?: string }[]) ?? [];
  const pets = (c.pets as string[]) ?? [];
  const referrer = c.referred_by_id ? contacts.find((x) => x.id === c.referred_by_id) : null;
  const sorted = [...mine].sort((a, b) => (a.outcome === 'open' || a.outcome === 'parked' ? 0 : 1) - (b.outcome === 'open' || b.outcome === 'parked' ? 0 : 1) || String(b.opp.outcome_at ?? b.opp.created_at).localeCompare(String(a.opp.outcome_at ?? a.opp.created_at)));

  const cell = (key: string, placeholder?: string, type?: string) => (canEdit ? <Edit className="cell" type={type} value={c[key] ?? ''} placeholder={placeholder} onSave={(v) => save({ [key]: v || null })} /> : <span className={type === 'email' ? '' : 'num'}>{c[key] || <small>—</small>}</span>);

  return (
    <div className="stack">
      <div className="pohead">
        <div>
          <h1 className="poname">{contactName(c)}</h1>
          <div className="small">
            {c.preferred_name ? `Goes by ${c.preferred_name} · ` : ''}
            {c.type ?? 'no type yet'}
            {c.company && contactName(c) !== c.company ? ` · ${c.company}` : ''}
            {referrer && (
              <>
                {' '}
                · referred by <Link to={`/people?contact=${referrer.id}`}>{contactName(referrer)}</Link>
              </>
            )}
            {` · since ${new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
          </div>
        </div>
        <div className="pwonlost num">
          <b>
            {won} won · {lost} lost · {open} open
          </b>
          {won + lost > 0 && <small>{Math.round((won / (won + lost)) * 100)}% win rate</small>}
        </div>
      </div>
      <div className="pcgrid">
        <Panel>
          <table className="pdetails">
            <tbody>
              <tr>
                <td className="lb">Phone</td>
                <td>{cell('phone')}</td>
              </tr>
              <tr>
                <td className="lb">Email</td>
                <td>{cell('email', undefined, 'email')}</td>
              </tr>
              <tr>
                <td className="lb">Address</td>
                <td>{cell('address')}</td>
              </tr>
              <tr>
                <td className="lb">Goes by</td>
                <td>{cell('preferred_name', 'preferred name')}</td>
              </tr>
              <tr>
                <td className="lb">Type</td>
                <td>
                  {canEdit ? (
                    <select className="cell" value={c.type ?? ''} onChange={(e) => void save({ type: e.target.value || null })}>
                      <option value="">—</option>
                      {[...new Set([...types, ...(c.type ? [c.type as string] : [])])].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  ) : (
                    c.type ?? <small>—</small>
                  )}
                </td>
              </tr>
              <tr>
                <td className="lb">Company</td>
                <td>{cell('company')}</td>
              </tr>
              <tr>
                <td className="lb">Referred by</td>
                <td>
                  {canEdit ? (
                    <select className="cell" value={c.referred_by_id ?? ''} onChange={(e) => void save({ referred_by_id: e.target.value || null })}>
                      <option value="">—</option>
                      {contacts.filter((x) => x.id !== c.id).map((x) => (
                        <option key={x.id} value={x.id}>
                          {contactName(x)}
                        </option>
                      ))}
                    </select>
                  ) : referrer ? (
                    contactName(referrer)
                  ) : (
                    <small>—</small>
                  )}
                </td>
              </tr>
              <tr>
                <td className="lb">QuickBooks</td>
                <td>{cell('quickbooks_customer_id', 'customer id')}</td>
              </tr>
              <tr>
                <td className="lb">Partner</td>
                <td>{cell('spouse', 'spouse or partner')}</td>
              </tr>
              <tr>
                <td className="lb">Kids</td>
                <td>
                  {kids.map((k, i) => (
                    <div key={i} className="row mid" style={{ gap: 6, marginBottom: 2 }}>
                      {canEdit ? <Edit className="cell" value={k.name} onSave={(v) => save({ kids: kids.map((x, n) => (n === i ? { ...x, name: v } : x)) })} /> : <span>{k.name}</span>}
                      {canEdit ? <Edit className="cell" type="date" value={k.birthdate ?? ''} onSave={(v) => save({ kids: kids.map((x, n) => (n === i ? { ...x, birthdate: v } : x)) })} /> : null}
                      <span className="nowrap">{age(k.birthdate) !== null ? `(${age(k.birthdate)})` : <small>birth date → age</small>}</span>
                      {canEdit && (
                        <Btn small title="Remove this kid" onClick={() => window.confirm(`Remove ${k.name || 'this kid'}?`) && void save({ kids: kids.filter((_, n) => n !== i) })}>
                          ×
                        </Btn>
                      )}
                    </div>
                  ))}
                  {kids.length === 0 && !canEdit && <small>None noted</small>}
                  {canEdit && (
                    <Btn small onClick={() => void save({ kids: [...kids, { name: 'Name', birthdate: '' }] })}>
                      + Kid
                    </Btn>
                  )}
                </td>
              </tr>
              <tr>
                <td className="lb">Pets</td>
                <td>{canEdit ? <Edit className="cell" value={pets.join(', ')} placeholder="names, comma separated" onSave={(v) => save({ pets: v ? v.split(',').map((x) => x.trim()).filter(Boolean) : [] })} /> : pets.join(', ') || <small>None noted</small>}</td>
              </tr>
              <tr>
                <td className="lb">Reach by</td>
                <td>{cell('reach_by', 'Text first, mornings')}</td>
              </tr>
              <tr>
                <td className="lb">Notes</td>
                <td>
                  {canEdit && (
                    <form
                      className="row mid"
                      style={{ marginBottom: 4 }}
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!note.trim()) return;
                        await insertRow('contact_note', { contact_id: c.id, body: note.trim(), by_user_id: s.user?.id ?? null });
                        setNote('');
                      }}
                    >
                      <input type="text" className="grow" value={note} placeholder="Add a dated note" onChange={(e) => setNote(e.target.value)} />
                      <Btn small type="submit" disabled={!note.trim()}>
                        Add
                      </Btn>
                    </form>
                  )}
                  {notes.rows.length === 0 && <small>None noted</small>}
                  {notes.rows.map((n) => (
                    <div key={n.id} className="small">
                      <span className="num">{shortDate(n.noted_at)}</span> · {n.body}
                    </div>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
          {canEdit && (
            <div style={{ marginTop: 10 }}>
              <div className="form">
                <label className="field">
                  <span>First name</span>
                  <Edit value={c.first_name ?? ''} onSave={(v) => save({ first_name: v || null })} />
                </label>
                <label className="field">
                  <span>Last name</span>
                  <Edit value={c.last_name ?? ''} onSave={(v) => save({ last_name: v || null })} />
                </label>
              </div>
              <div style={{ marginTop: 10 }}>
                <Btn
                  small
                  onClick={async () => {
                    if (!window.confirm(`Delete ${contactName(c)}? Their notes go too.`)) return;
                    onDeleted();
                    await deleteRow('contact', c.id);
                  }}
                >
                  Delete contact
                </Btn>
              </div>
            </div>
          )}
        </Panel>
        <div>
          <div className="lb" style={{ marginBottom: 6 }}>
            Opportunities · {mine.length}
          </div>
          {p.loading && <Loading />}
          {!p.loading && mine.length === 0 && <Empty>No opportunities with this contact yet.</Empty>}
          <div className="pclist">
            {sorted.map((l) => (
              <OppRow key={l.id} p={p} lead={l} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OppRow({ p, lead }: { p: Pipeline; lead: Lead }) {
  const seat = seatWords(p, lead.step);
  const words =
    lead.outcome === 'won'
      ? `Won ${monthDay(lead.opp.outcome_at)}${lead.opp.anchor_type ? ` · ${lead.opp.anchor_type.toLowerCase()} ${monthDay(lead.opp.anchor_date)}` : ''}`
      : lead.outcome === 'lost'
        ? `Lost ${monthDay(lead.opp.outcome_at)}${lead.opp.outcome_note ? ` · ${lead.opp.outcome_note}` : ''}`
        : lead.outcome === 'parked'
          ? `Parked · re-approach ${lead.nextTouch ? monthDay(lead.nextTouch) : 'not set'}`
          : lead.step
            ? lead.step.name
            : `${lead.stage?.name ?? 'Open'} · not placed`;
  return (
    <div className={`pcl${lead.needsOwner ? ' needs' : ''}`}>
      <span>
        <Link to={`/opportunities/${lead.id}`} className="rowlink">
          <b>{lead.name}</b>
        </Link>
        <br />
        <small>
          {words}
          {lead.outcome === 'open' && lead.step && (
            <>
              {' '}
              · {seat.you ? <span className="pyou">You</span> : <span className="pown">{seat.words}</span>}
            </>
          )}
        </small>
      </span>
      <span>{lead.outcome === 'open' ? <Strip lead={lead} /> : null}</span>
      <span className="right">
        <LeadValue lead={lead} />
      </span>
    </div>
  );
}
